import clsx from "clsx";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import type {
  ActivityEventView,
  CaseDetail,
  CursorPage,
  PartLineView,
} from "../../../../shared/contracts.js";
import { caseQueryKeys, fetchCaseActivityPage } from "../../../api/caseQueries.js";
import { describeError } from "../../../ui/AsyncPanel.js";
import {
  etaLabel,
  exceptionLabel,
  remainingQuantity,
} from "../parts-actions/VerifyArrivalDialog.js";
import { technicianLabel, vinEndingLabel } from "../presentation.js";
import { ActivityTimeline, formatInstant } from "./ActivityTimeline.js";
import { activityContext, type ActivityContext } from "./activity-sentences.js";

/**
 * The read-only body of a case, shared by the Parts inspector and the
 * shared-workspace detail (spec §10 "Right inspector", §11 "Case detail"):
 * RO and VIN ending, advisor and technician, appointment, vendor reference
 * and last update; the order note; every part line with ordered and verified
 * quantities, ETA or No ETA and any exception; the comments; and the
 * immutable activity history with older pages on request.
 *
 * Everything is content, never a disabled form. Status, totals, permissions
 * and the 48-hour window are the server's — nothing here recomputes them.
 * The component adds no live region of its own (`role="status"`/`"alert"`):
 * the surrounding inspector owns announcements.
 *
 * The parts table ({@link PartLinesTable}) and the activity history with
 * its older pages ({@link ActivityHistory}) are exported on their own: the
 * wholesale inspector shows the same lines and the same timeline for an
 * order on the other side of the counter (wholesale spec §4.4).
 */

export interface CaseDetailContentProps {
  detail: CaseDetail;
  className?: string;
}

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Chicago",
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** A calendar date (`YYYY-MM-DD`), shown as a date rather than an instant. */
export function formatCalendarDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return value;
  return dateFormatter.format(new Date(Date.UTC(year, month - 1, day, 12)));
}

export function CaseDetailContent({ detail, className }: CaseDetailContentProps) {
  const { case: record, partLines, comments, activity } = detail;
  const noteTitleId = useId();
  const linesTitleId = useId();
  const commentsTitleId = useId();
  const activityTitleId = useId();

  return (
    <div className={clsx("case-detail", className)}>
      <dl className="inspector__facts">
        <div className="inspector__fact">
          <dt>RO</dt>
          <dd>{record.roNumber ?? "Pre-RO"}</dd>
        </div>
        <div className="inspector__fact">
          <dt>VIN ending</dt>
          <dd className="inspector__mono">{vinEndingLabel(record.vinEnding)}</dd>
        </div>
        <div className="inspector__fact">
          <dt>Advisor</dt>
          <dd>{record.advisorName}</dd>
        </div>
        <div className="inspector__fact">
          <dt>Technician</dt>
          <dd>{technicianLabel(record.technicianName)}</dd>
        </div>
        <div className="inspector__fact">
          <dt>Appointment</dt>
          <dd>
            {record.appointmentDate
              ? formatCalendarDate(record.appointmentDate)
              : "—"}
          </dd>
        </div>
        <div className="inspector__fact">
          <dt>Vendor reference</dt>
          <dd>{record.vendorOrderReference || "—"}</dd>
        </div>
        <div className="inspector__fact">
          <dt>Updated</dt>
          <dd>{formatInstant(record.updatedAt)}</dd>
        </div>
      </dl>

      {record.orderNote ? (
        <section className="inspector__section" aria-labelledby={noteTitleId}>
          <h3 id={noteTitleId} className="inspector__section-title">
            Order note
          </h3>
          <p className="inspector__note">{record.orderNote}</p>
        </section>
      ) : null}

      <section className="inspector__section" aria-labelledby={linesTitleId}>
        <h3 id={linesTitleId} className="inspector__section-title">
          Parts ordered
        </h3>
        <PartLinesTable partLines={partLines} />
      </section>

      <section className="inspector__section" aria-labelledby={commentsTitleId}>
        <h3 id={commentsTitleId} className="inspector__section-title">
          Comments <span className="inspector__count">({comments.length})</span>
        </h3>
        {comments.length === 0 ? (
          <p className="inspector__empty">No comments yet.</p>
        ) : (
          <ul className="inspector__comments">
            {comments.map((comment) => (
              <li key={comment.id} className="inspector__comment">
                <p className="inspector__comment-meta">
                  <span className="inspector__comment-author">
                    {comment.authorName}
                  </span>{" "}
                  <time dateTime={comment.createdAt}>
                    {formatInstant(comment.createdAt)}
                  </time>
                </p>
                <p className="inspector__comment-body">{comment.body}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="inspector__section" aria-labelledby={activityTitleId}>
        <h3 id={activityTitleId} className="inspector__section-title">
          Activity
        </h3>
        <ActivityHistory
          ownerId={record.id}
          firstPage={activity}
          context={activityContext(detail)}
          olderKey={caseQueryKeys.olderActivity(record.id, activity.nextCursor)}
          fetchOlderPage={(cursor) => fetchCaseActivityPage(record.id, cursor)}
        />
      </section>
    </div>
  );
}

/** The lines of an order, on either side: part, verified of ordered, ETA and any exception. */
export function PartLinesTable({ partLines }: { partLines: readonly PartLineView[] }) {
  return (
    <table className="inspector__lines">
      <thead>
        <tr>
          <th scope="col">Part</th>
          <th scope="col">Verified</th>
          <th scope="col">ETA</th>
          <th scope="col">Exception</th>
        </tr>
      </thead>
      <tbody>
        {partLines.map((line) => (
          <PartLineRow key={line.id} line={line} />
        ))}
      </tbody>
    </table>
  );
}

export interface ActivityHistoryProps {
  /** The case or wholesale order the events belong to. */
  ownerId: string;
  firstPage: CursorPage<ActivityEventView>;
  context: ActivityContext;
  /** The cache key of the older pages, nested under the owner's detail and keyed by the first page's cursor. */
  olderKey: readonly unknown[];
  /** One page of older events, starting at `cursor`, as the owner's detail route returns it. */
  fetchOlderPage: (cursor: string) => Promise<CursorPage<ActivityEventView>>;
}

/**
 * The detail's first page of activity plus, on request, the older pages the
 * server paginates behind `activity.nextCursor`. Nothing loads until the
 * person asks; each "Show older" then follows the cursor the previous page
 * ended on. The timeline collapses older history; it never discards it.
 */
export function ActivityHistory({ ownerId, firstPage, context, olderKey, fetchOlderPage }: ActivityHistoryProps) {
  const [wantOlder, setWantOlder] = useState(false);
  const fromCursor = firstPage.nextCursor;
  const older = useInfiniteQuery({
    queryKey: olderKey,
    queryFn: ({ pageParam }) => fetchOlderPage(pageParam ?? ""),
    initialPageParam: fromCursor,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: wantOlder && fromCursor !== null,
  });

  // Another order, or a refetched first page with a new cursor: start over.
  useEffect(() => {
    setWantOlder(false);
  }, [ownerId, fromCursor]);

  const olderEvents = older.data?.pages.flatMap((page) => page.items) ?? [];
  const events = [...firstPage.items, ...olderEvents];
  const hasOlder = wantOlder
    ? older.hasNextPage || older.isPending || older.isError
    : firstPage.nextCursor !== null;

  const showOlder = () => {
    if (!wantOlder) {
      setWantOlder(true);
      return;
    }
    if (older.isError) {
      void older.refetch();
      return;
    }
    if (older.hasNextPage) void older.fetchNextPage();
  };

  return (
    <ActivityTimeline
      events={events}
      context={context}
      hasOlder={hasOlder}
      onShowOlder={showOlder}
      isLoadingOlder={
        wantOlder && (older.isPending || older.isFetchingNextPage)
      }
      olderError={
        older.isError
          ? `Older activity didn't load: ${describeError(older.error).description}`
          : null
      }
    />
  );
}

function PartLineRow({ line }: { line: PartLineView }) {
  const remaining = remainingQuantity(line);
  return (
    <tr
      className="inspector__line"
      data-complete={remaining === 0 ? "true" : undefined}
    >
      <td>
        <span className="inspector__mono inspector__part-number">
          {line.partNumber}
        </span>
        <span className="inspector__part-description">{line.description}</span>
      </td>
      <td className="inspector__quantity">
        {line.quantityVerified} of {line.quantityOrdered}
      </td>
      <td>{etaLabel(line.eta)}</td>
      <td>
        {line.exception ? (
          <span
            className="inspector__exception"
            data-exception={line.exception}
          >
            {exceptionLabel(line.exception)}
            {line.exceptionReason ? ` — ${line.exceptionReason}` : ""}
          </span>
        ) : (
          <span className="inspector__muted">—</span>
        )}
      </td>
    </tr>
  );
}
